import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell 
} from 'recharts';
import { 
  Users, Activity, TrendingUp, AlertTriangle, MapPin, 
  Building2, Eye, FileText, Bell, RefreshCw, ChevronRight, UserPlus, UserCog, Shield, Download
} from 'lucide-react';
import Navbar from '@/components/Navbar';
import { api } from '@/utils/api';
import { useLanguage } from '@/LanguageContext';

const DistrictAdminDashboard = ({ user, onLogout }) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Get user's assigned districts and state
  const assignedDistricts = user?.assigned_districts || [];
  const assignedStates = user?.assigned_states || [];
  const primaryState = user?.primary_state;
  const primaryDistrict = assignedDistricts[0]; // Use first assigned district

  const handleExportICDS = async () => {
    try {
      setExporting(true);
      const response = await api.exportICDSReport(primaryState, primaryDistrict);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ICDS_ECD_Report_${primaryState || 'All'}_${primaryDistrict || 'All'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting ICDS report:', error);
      alert('Failed to export report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      // Determine state code
      let stateCode = primaryState;
      if (!stateCode && assignedStates.length > 0) {
        stateCode = assignedStates[0];
      }
      
      // If we have state and district, fetch comprehensive dashboard
      if (stateCode && primaryDistrict) {
        const res = await api.getDistrictAdminDashboard(stateCode, primaryDistrict);
        setDashboard(res.data);
      } else {
        // Fallback to basic admin dashboard
        const res = await api.getAdminDashboard();
        setDashboard({
          overview: res.data?.overview || {},
          risk_distribution: res.data?.risk_distribution || {},
          taluk_breakdown: [],
          location_breakdown: [],
          recent_screenings: [],
          high_risk_cases: []
        });
      }
    } catch (error) {
      console.error('Error fetching district data:', error);
      setError('Failed to load dashboard data');
      // Try fallback
      try {
        const res = await api.getAdminDashboard();
        setDashboard({
          overview: res.data?.overview || {},
          risk_distribution: res.data?.risk_distribution || {},
          taluk_breakdown: [],
          location_breakdown: [],
          recent_screenings: [],
          high_risk_cases: []
        });
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9]">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-bold text-[#0F4C81]">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // Prepare chart data
  const riskChartData = [
    { name: t('admin.lowRisk'), value: dashboard?.risk_distribution?.low || 0, color: '#10B981' },
    { name: t('admin.moderateRisk'), value: dashboard?.risk_distribution?.moderate || 0, color: '#F59E0B' },
    { name: t('admin.highRisk'), value: dashboard?.risk_distribution?.high || 0, color: '#EF4444' }
  ];

  // Taluk breakdown data for charts
  const talukBreakdown = dashboard?.taluk_breakdown || [];
  const talukChartData = talukBreakdown.slice(0, 8).map(t => ({
    name: t.taluk,
    children: t.total_children,
    highRisk: t.high_risk
  }));

  // Location breakdown
  const locationBreakdown = dashboard?.location_breakdown || [];

  // Age distribution
  const ageData = dashboard?.age_distribution 
    ? Object.entries(dashboard.age_distribution).map(([age, count]) => ({
        name: `${age} yrs`,
        value: count
      }))
    : [];

  return (
    <>
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="district-admin-dashboard">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-4">
            <div className="bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] p-3 rounded-2xl shadow-lg">
              <Building2 className="text-white" size={32} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#111827] font-['Nunito']" data-testid="dashboard-title">
                {dashboard?.district || primaryDistrict || 'District'} Dashboard
              </h1>
              <div className="flex items-center space-x-2 mt-1">
                <MapPin size={16} className="text-[#6B7280]" />
                <p className="text-base text-[#4B5563]">
                  {dashboard?.state_name || primaryState || 'State'} → {dashboard?.district || primaryDistrict || 'District'}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4 md:mt-0 flex flex-wrap gap-3">
            <button
              onClick={() => navigate('/user-management')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#10B981] text-white rounded-xl font-semibold hover:bg-[#059669] transition-colors shadow-md"
              data-testid="user-management-button"
            >
              <UserCog size={18} />
              <span>{t('admin.userManagement')}</span>
            </button>
            <button
              onClick={() => navigate('/audit-logs')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors shadow-md"
              data-testid="audit-logs-button"
            >
              <Shield size={18} />
              <span>{t('admin.viewAuditLogs')}</span>
            </button>
            <button
              onClick={() => navigate('/admin/create-user')}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#F59E0B] text-white rounded-xl font-semibold hover:bg-[#D97706] transition-colors shadow-md"
              data-testid="create-user-button"
            >
              <UserPlus size={18} />
              <span>Create User</span>
            </button>
            <button
              onClick={handleExportICDS}
              disabled={exporting}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#059669] text-white rounded-xl font-semibold hover:bg-[#047857] transition-colors shadow-md"
              data-testid="icds-export-button"
            >
              <Download size={18} className={exporting ? 'animate-pulse' : ''} />
              <span>{exporting ? 'Exporting...' : 'ICDS Export'}</span>
            </button>
            <button
              onClick={fetchData}
              disabled={refreshing}
              className="flex items-center justify-center space-x-2 px-4 py-2 bg-white text-[#8B5CF6] rounded-xl font-semibold border-2 border-[#8B5CF6] hover:bg-[#8B5CF6] hover:text-white transition-colors"
              data-testid="refresh-button"
            >
              <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-children">
            <Users className="text-[#3B82F6] mb-2" size={28} />
            <span className="text-2xl font-bold text-[#111827]">{dashboard?.overview?.total_children || 0}</span>
            <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">{t('dashboard.totalChildren')}</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-screenings">
            <Activity className="text-[#10B981] mb-2" size={28} />
            <span className="text-2xl font-bold text-[#111827]">{dashboard?.overview?.total_screenings || 0}</span>
            <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">{t('admin.screeningsDone')}</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-coverage">
            <TrendingUp className="text-[#8B5CF6] mb-2" size={28} />
            <span className="text-2xl font-bold text-[#111827]">{dashboard?.overview?.coverage_rate || 0}%</span>
            <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Coverage</p>
          </div>

          <div className="rounded-xl bg-red-50 p-5 shadow-sm border-2 border-red-200 hover:shadow-md transition-shadow" data-testid="stat-high-risk">
            <AlertTriangle className="text-[#EF4444] mb-2" size={28} />
            <span className="text-2xl font-bold text-[#EF4444]">{dashboard?.risk_distribution?.high || 0}</span>
            <p className="text-xs font-semibold text-[#991B1B] uppercase mt-1">{t('dashboard.highRisk')}</p>
          </div>

          <div className="rounded-xl bg-yellow-50 p-5 shadow-sm border-2 border-yellow-200 hover:shadow-md transition-shadow" data-testid="stat-pending">
            <Activity className="text-[#F59E0B] mb-2" size={28} />
            <span className="text-2xl font-bold text-[#F59E0B]">{dashboard?.overview?.pending_screenings || 0}</span>
            <p className="text-xs font-semibold text-[#92400E] uppercase mt-1">Pending</p>
          </div>

          <div className="rounded-xl bg-white p-5 shadow-sm border-2 border-slate-100 hover:shadow-md transition-shadow" data-testid="stat-taluks">
            <MapPin className="text-[#6366F1] mb-2" size={28} />
            <span className="text-2xl font-bold text-[#111827]">{dashboard?.total_taluks || talukBreakdown.length}</span>
            <p className="text-xs font-semibold text-[#4B5563] uppercase mt-1">Taluks</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Risk Distribution */}
          <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100" data-testid="risk-distribution-chart">
            <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">{t('admin.riskDistribution')}</h3>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie 
                  data={riskChartData} 
                  cx="50%" 
                  cy="50%" 
                  innerRadius={50}
                  outerRadius={80} 
                  paddingAngle={5}
                  dataKey="value" 
                  label={(entry) => `${entry.value}`}
                >
                  {riskChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-4 mt-2 text-xs">
              {riskChartData.map((entry, index) => (
                <div key={index} className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                  <span>{entry.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Age Distribution */}
          {ageData.length > 0 && (
            <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
              <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">Age Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={ageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#6366F1" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Taluk-wise Chart */}
          {talukChartData.length > 0 && (
            <div className="rounded-xl bg-white p-6 shadow-sm border-2 border-slate-100">
              <h3 className="text-xl font-bold text-[#111827] mb-4 font-['Nunito']">Children by Taluk</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={talukChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Bar dataKey="children" fill="#0F4C81" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Taluk-wise Analytics Table */}
        {talukBreakdown.length > 0 && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-8" data-testid="taluk-table">
            <h2 className="text-2xl font-bold text-[#111827] mb-4 flex items-center font-['Nunito']">
              <MapPin className="mr-2 text-[#0F4C81]" size={24} />
              Taluk-wise Analytics
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left py-3 px-4 font-bold text-[#374151]">Taluk</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Children</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Screenings</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Coverage</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">High Risk</th>
                    <th className="text-center py-3 px-4 font-bold text-[#374151]">Risk Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {talukBreakdown.map((taluk, index) => (
                    <tr key={taluk.taluk} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="py-3 px-4 font-semibold text-[#111827]">{taluk.taluk}</td>
                      <td className="py-3 px-4 text-center">{taluk.total_children}</td>
                      <td className="py-3 px-4 text-center">{taluk.total_screenings}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          taluk.coverage_rate >= 80 ? 'bg-green-100 text-green-700' :
                          taluk.coverage_rate >= 50 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {taluk.coverage_rate}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {taluk.high_risk > 0 ? (
                          <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-bold">
                            {taluk.high_risk}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={`text-xs font-semibold ${
                          taluk.risk_rate > 20 ? 'text-red-600' :
                          taluk.risk_rate > 10 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {taluk.risk_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* City/Village Breakdown */}
        {locationBreakdown.length > 0 && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm mb-8" data-testid="location-breakdown">
            <h2 className="text-2xl font-bold text-[#111827] mb-4 flex items-center font-['Nunito']">
              <Building2 className="mr-2 text-[#6366F1]" size={24} />
              City/Village-wise Analytics
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto">
              {locationBreakdown.map((location, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-4 border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-bold text-[#111827] truncate">{location.city_village}</p>
                    {location.high_risk > 0 && (
                      <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold">
                        {location.high_risk} HR
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#6B7280] mb-2">{location.taluk}</p>
                  <div className="flex justify-between text-xs">
                    <span className="text-[#4B5563]">Children: <strong>{location.total_children}</strong></span>
                    <span className={`font-bold ${
                      location.coverage_rate >= 80 ? 'text-green-600' :
                      location.coverage_rate >= 50 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {location.coverage_rate}% covered
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* High Risk Cases & Recent Screenings Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* High Risk Cases */}
          {dashboard?.high_risk_cases?.length > 0 && (
            <div className="bg-white rounded-2xl border-2 border-red-100 p-6 shadow-sm" data-testid="high-risk-cases">
              <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center font-['Nunito']">
                <AlertTriangle className="mr-2 text-[#EF4444]" size={20} />
                High-Risk Cases Needing Attention
              </h3>
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {dashboard.high_risk_cases.map((hrCase, index) => (
                  <div 
                    key={index} 
                    className="bg-red-50 rounded-lg p-3 border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                    onClick={() => navigate(`/child/${hrCase.child_id}`)}
                  >
                    <p className="font-semibold text-[#111827]">{hrCase.child_name}</p>
                    <div className="flex justify-between text-xs text-[#6B7280] mt-1">
                      <span>{hrCase.taluk} • {hrCase.city_village}</span>
                      <ChevronRight size={16} className="text-red-400" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Screenings */}
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm" data-testid="recent-screenings">
            <h3 className="text-xl font-bold text-[#111827] mb-4 flex items-center font-['Nunito']">
              <Activity className="mr-2 text-[#10B981]" size={20} />
              Recent Screenings
            </h3>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {dashboard?.recent_screenings?.slice(0, 10).map((screening, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-3 bg-[#F3F4F6] rounded-lg cursor-pointer hover:bg-slate-200 transition-colors"
                  onClick={() => navigate(`/child/${screening.child_id}`)}
                >
                  <div>
                    <p className="font-semibold text-[#111827]">{screening.child_name || 'Unknown'}</p>
                    <p className="text-xs text-[#6B7280]">{screening.taluk} • {screening.city_village}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                    screening.risk_level === 'high' ? 'bg-red-100 text-red-700' :
                    screening.risk_level === 'moderate' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>
                    {screening.risk_level?.toUpperCase() || 'N/A'}
                  </span>
                </div>
              ))}
              {(!dashboard?.recent_screenings || dashboard.recent_screenings.length === 0) && (
                <div className="text-center py-8 text-[#6B7280]">
                  <Activity size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No recent screenings</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Empty State when no data */}
        {talukBreakdown.length === 0 && locationBreakdown.length === 0 && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center mb-8">
            <Users size={64} className="mx-auto mb-4 text-[#9CA3AF]" />
            <h3 className="text-xl font-bold text-[#111827] mb-2">No Location Data Yet</h3>
            <p className="text-[#6B7280] mb-4">
              Children with Taluk and City/Village details will appear here once registered.
            </p>
            <button
              onClick={() => navigate('/add-child')}
              className="px-6 py-3 bg-[#0F4C81] text-white rounded-xl font-semibold hover:bg-[#0d3d6b] transition-colors"
            >
              Register First Child
            </button>
          </div>
        )}
      </div>
    </div>
    
    {/* Copyright Footer */}
    <footer className="bg-[#F3F4F6] border-t border-gray-200 py-3 text-center">
      <p className="text-sm text-[#6B7280]">© 2025 Hamrith Consulting Services LLP (HCS). All Rights Reserved.</p>
    </footer>
    </>
  );
};

export default DistrictAdminDashboard;
